import {REQUIREMENT_BILLING_TYPE_OPTIONS, type RequirementBillingType} from 'src/jobs/billing-types'
import type {ProjectBillingMethod} from 'src/projects/schemas'

export const ORDER_BILLING_CHOICES: {
    method: ProjectBillingMethod
    title: string
    description: string
}[] = [
    {
        method: 'line_rates',
        title: 'Each line at its own rate',
        description: 'Each crew and equipment line at its own rate and billing type',
    },
    {
        method: 'hourly_transportation',
        title: 'One all-in hourly rate',
        description:
            'One hourly rate for crew, trucks and fuel, plus rental equipment at its own rate',
    },
    {
        method: 'per_load',
        title: 'Per load',
        description: 'A price per load, plus rental equipment at its own rate',
    },
    {
        method: 'flat_rate',
        title: 'Flat',
        description: 'A fixed price for crew and trucks, plus rental equipment at its own rate',
    },
]

export function billsEachLine(method: ProjectBillingMethod | null | undefined): boolean {
    return method == null || method === 'line_rates'
}

export function orderRatePhrase(method: ProjectBillingMethod): string {
    switch (method) {
        case 'line_rates':
            return 'each line at its own rate'
        case 'hourly_transportation':
            return 'at one all-in hourly rate'
        case 'per_load':
            return 'per load'
        case 'flat_rate':
            return 'at a flat rate'
    }
}

type RatedLine = {
    billingRate?: string | null
    billingType?: string | null
    rateFromJob?: string | null
}

/** Clear the rate fields, including `rateFromJob`. */
function withoutLineRate<Row extends RatedLine>(row: Row): Row {
    return row.billingRate == null && row.billingType == null && row.rateFromJob === undefined
        ? row
        : {...row, billingRate: null, billingType: null, rateFromJob: undefined}
}

const RENTAL_BILLING_TYPES: RequirementBillingType[] = ['daily', 'weekly', 'monthly']

export const RENTAL_BILLING_TYPE_OPTIONS = REQUIREMENT_BILLING_TYPE_OPTIONS.filter((option) =>
    RENTAL_BILLING_TYPES.includes(option.value),
)

export function lineTakesRate(
    method: ProjectBillingMethod | null | undefined,
    line: {isRentalEquipment: boolean},
): boolean {
    return billsEachLine(method) || line.isRentalEquipment
}

/** Clear the line rate unless the method bills each line or the row keeps a rental billing type. */
export function lineAsBilled<Row extends RatedLine>(
    method: ProjectBillingMethod | null | undefined,
    row: Row,
    isRentalEquipment: boolean,
): Row {
    if (billsEachLine(method)) {
        return row
    }

    const keeps =
        isRentalEquipment &&
        (row.billingType == null || RENTAL_BILLING_TYPES.some((type) => type === row.billingType))
    return keeps ? row : withoutLineRate(row)
}

export function requirementsAsBilled<Row extends RatedLine>(
    method: ProjectBillingMethod | null | undefined,
    rows: Row[],
    isRentalEquipment: (row: Row) => boolean,
): Row[] {
    return rows.map((row) => {
        const billed = lineAsBilled(method, row, isRentalEquipment(row))
        return billed === row ? row : {...row, billingRate: null, billingType: null}
    })
}
